// react
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// types
import { ActionStatus, Invoice, InvoicePayload } from '../../../@types';

// mui
import { Button, Grid, SelectChangeEvent } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';
import { SelectCurrency } from '../../fields/SelectCurrency/SelectCurrency';
import { patchInvoice } from '../../../services/invoices';

export interface FormEditInvoiceProps {
	invoice: Invoice;
	onCancel: () => void;
	onSuccess: (e: FormEvent<HTMLFormElement>, invoice: Invoice) => void;
}

export const FormEditInvoice = ({ invoice, onCancel, onSuccess }: FormEditInvoiceProps) => {
	// hooks
	const { t } = useTranslation();

	// state
	const [values, setValues] = useState<Partial<InvoicePayload>>({
		amount: invoice.amount,
		currency: invoice.currency,
		num: invoice.num,
		tax: invoice.tax,
	});

	const [status, setStatus] = useState<ActionStatus>('DEFAULT');

	// vars
	const formId = 'form-edit-invoice';

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues((currentValues) => ({
			...currentValues,
			[name]: name === 'amount' ? Number(value) : value,
		}));
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setStatus('LOADING');
		try {
			const invoiceUpdated = await patchInvoice(invoice.id, {
				num: values?.num?.trim(),
				amount: Number(values?.amount),
				currency: values?.currency?.trim().toUpperCase(),
				tax: Number(values?.tax),
			});
			onSuccess(e, invoiceUpdated);
		} catch (error) {
			console.error('Error updating invoice:', error);
		} finally {
			setStatus('DEFAULT');
		}
	};

	// update values when invoice prop changes
	useEffect(() => {
		setValues({
			amount: invoice.amount,
			currency: invoice.currency,
			num: invoice.num,
			tax: invoice.tax,
		});
	}, [invoice]);

	return (
		<form onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Input
						id={`${formId}-number`}
						label={t('invoice')}
						name="num"
						required={true}
						size="small"
						value={values.num}
						onChange={handleChangeValue}
					/>
				</Grid>
				<Grid item xs={12} md={4}>
					<SelectCurrency
						id={`${formId}-currency`}
						label={t('currency')}
						name="currency"
						onChange={handleChangeValue}
						required={true}
						value={values.currency}
					/>
				</Grid>
				<Grid item xs={12} md={4}>
					<Input
						id={`${formId}-amount`}
						inputProps={{ min: 0, step: '0.01' }}
						label={t('amount')}
						name="amount"
						required={true}
						size="small"
						type="number"
						value={values.amount}
						onChange={handleChangeValue}
					/>
				</Grid>
				<Grid item xs={12} md={4}>
					<Input
						id={`${formId}-tax`}
						inputProps={{ min: 0, step: '0.01' }}
						label={t('tax')}
						name="tax"
						size="small"
						type="number"
						value={values.tax}
						onChange={handleChangeValue}
					/>
				</Grid>
				<Grid container item justifyContent="flex-end" spacing={1} xs={12}>
					<Grid item>
						<Button onClick={onCancel} variant="outlined">
							{t('cancel')}
						</Button>
					</Grid>
					<Grid item>
						<Button type="submit" variant="contained">
							{status === 'LOADING' ? t('loading') : t('update')}
						</Button>
					</Grid>
				</Grid>
			</Grid>
		</form>
	);
};
